import { Fragment, useRef, useLayoutEffect, type ReactNode } from 'react';
import { Disclosure } from '@headlessui/react';
import { useLocation } from 'react-router-dom';
import { Heading, Paragraph } from '@hungryroot/ramen';
import styles from './FoodProfileDisclosure.module.css';

interface Props {
  testid?: string;
  children: ReactNode;
  title: string;
  subtitle: string;
  icon: ReactNode;
  deepLink?: string;
  headerLinkText?: string;
}

export function FoodProfileDisclosure(props: Props) {
  const { testid, children, title, icon, subtitle, deepLink, headerLinkText } =
    props;

  const { hash } = useLocation();
  const isDeepLinked = deepLink !== undefined && hash.includes(deepLink);
  const ref = useRef<null | HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (isDeepLinked) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start',
      });
    }
  }, [isDeepLinked]);

  return (
    <Disclosure
      data-testid={testid}
      as="div"
      className={styles.container}
      defaultOpen={isDeepLinked}
      ref={ref}
    >
      {({ open }) => (
        <Fragment key="disclosure">
          <Disclosure.Button
            data-testid={`${testid}-button`}
            className={styles.button}
          >
            {icon}
            <div className={styles.text}>
              <Heading
                testid={`${testid}-title`}
                variant="H5"
                className={styles.title}
              >
                {title}
              </Heading>
              <Paragraph
                testid={`${testid}-subtitle`}
                className={styles.subtitle}
              >
                {subtitle}
              </Paragraph>
              {headerLinkText ? (
                // Looks like a link, but it just toggles the disclosure panel through event bubbling
                <Paragraph
                  testid={`${testid}-link`}
                  className={styles['header-link']}
                >
                  {headerLinkText}
                </Paragraph>
              ) : null}
            </div>
            {open ? (
              <span
                data-testid={`${testid}-toggle-minus`}
                className={styles['toggle-minus']}
              >
                -
              </span>
            ) : (
              <span
                data-testid={`${testid}-toggle-plus`}
                className={styles['toggle-icon']}
              >
                +
              </span>
            )}
          </Disclosure.Button>
          <Disclosure.Panel
            unmount={false}
            data-testid={`${testid}-panel`}
            as="div"
            className={styles.panel}
          >
            {children}
          </Disclosure.Panel>
        </Fragment>
      )}
    </Disclosure>
  );
}
